/**
 * The sitewide no-image treatment — one definition, used by every surface.
 *
 * FRIDGE-239. The owner's direction of 08/03/2026: when a card or a product has
 * no image, the slot shows the Fridge.com logo — and the logo appears ONLY in
 * that case, never over a real image.
 *
 * Before this module there were three different answers to the same question:
 * a blue→orange brand gradient on brand-page article cards, a flat grey
 * gradient on the brand-kit article card, and a bare canvas box on the product
 * image. Per the sitewide standard, a ruling on one card type is the ruling for
 * every card type, so the treatment lives here rather than in each renderer.
 *
 * This also collapses a real duplication: `isFridgeFallbackImage` was defined
 * twice, character-for-character, in `components/brand-kit/article-card.tsx`
 * and `app/blogs/news/[slug]/page-client.tsx`. Two copies of a regex that
 * decides how images are rendered is one copy away from them disagreeing.
 */

/** The brand wordmark used as the no-image mark. 2215×391 (≈5.7:1). */
export const FRIDGE_FALLBACK_LOGO = '/Fridge.com-brand-logos/Fridge.com-blue.png'

/**
 * True when a URL is already one of the Fridge.com placeholder assets.
 *
 * Distinct from "there is no image at all": these are rows whose stored image
 * IS the placeholder. Such images must be rendered CONTAINED (letterboxed on a
 * neutral background) rather than cover-cropped, or the wordmark gets sliced.
 * Callers use this to pick the fit, not to decide whether to show a fallback.
 */
export function isFridgeFallbackImage(url: string | null | undefined): boolean {
  if (!url) return false
  const trimmed = url.trim()
  if (!trimmed) return false
  return /(fallback-blog-hero|Fridge\.com-blue|Fridge\.com-logo)/i.test(trimmed)
}

/**
 * True when a slot has genuinely no usable image and the fallback mark should
 * render. Empty strings and whitespace count as absent — several callers pass
 * `''` rather than null for a missing column.
 */
export function hasNoImage(url: string | null | undefined): boolean {
  return !url || !String(url).trim()
}
